import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';
import {
  ListPageShell, ListPageHeader, ListSearchBar, ListFilterSelect, ListPagination,
} from '../components/ui/ListPageChrome';
import { useTableSort, SortTh } from '../components/ui/TableSort';

const STATUS_OPTS = ['Planned', 'Released', 'In Progress', 'Completed', 'Closed', 'Cancelled'];

const statusStyle = (s) => ({
  'Planned':      'bg-[#FEF5E7] text-[#B8860B]',
  'Released':     'bg-[#EDF2F7] text-[#4A6FA5]',
  'In Progress':  'bg-[#F1EEF6] text-[#7B6FA0]',
  'Completed':    'bg-[#EDF5F0] text-[#4A9E6E]',
  'Closed':       'bg-[#EDF5F0] text-[#4A9E6E]',
  'Cancelled':    'bg-[#F0F4F3] text-[#6B7F7F]',
}[s] || 'bg-[#F0F4F3] text-[#6B7F7F]');

export default function ProductionOrders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isSuperAdmin = user?.role_name === 'Super Admin';

  const [orders, setOrders]      = useState([]);
  const sort = useTableSort(orders);
  const [loading, setLoading]    = useState(true);
  const [search, setSearch]      = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage]          = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal]        = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const PER_PAGE = 25;

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/production-orders?page=${page}&limit=${PER_PAGE}`;
      if (statusFilter) url += `&status=${encodeURIComponent(statusFilter)}`;
      if (search)       url += `&search=${encodeURIComponent(search)}`;
      const data = await api.get(url);
      setOrders(data.rows || []);
      setTotalPages(data.totalPages || 1);
      setTotal(data.total || 0);
      setSelectedIds(new Set());
      setIsAllSelected(false);
    } catch { setOrders([]); }
    finally { setLoading(false); }
  }, [page, search, statusFilter, refreshKey]);

  useEffect(() => { loadOrders(); }, [loadOrders]);

  /* reset to page 1 when filters change */
  const applySearch = (v) => { setSearch(v); setPage(1); };
  const applyStatus = (v) => { setStatusFilter(v); setPage(1); };

  const handleSelectRow = (id) => {
    if (isAllSelected) {
      const next = new Set();
      orders.forEach(o => {
        if (o.prod_order_id !== id) {
          next.add(o.prod_order_id);
        }
      });
      setSelectedIds(next);
      setIsAllSelected(false);
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    }
  };

  const handleSelectAll = () => {
    const pageAllSelected = orders.length > 0 && orders.every(o => selectedIds.has(o.prod_order_id));
    if (isAllSelected || pageAllSelected) {
      setIsAllSelected(false);
      setSelectedIds(new Set());
    } else {
      setIsAllSelected(true);
      setSelectedIds(new Set());
    }
  };

  const handleBulkDelete = async () => {
    const count = isAllSelected ? total : selectedIds.size;
    if (count === 0) return;
    const msg = `Are you sure you want to delete the ${count} selected production order(s)? This will also delete their associated components.`;
    if (!window.confirm(msg)) return;

    try {
      if (isAllSelected) {
        await api.post('/production-orders/bulk-delete', {
          all: true,
          search: search || undefined,
          status: statusFilter || undefined
        });
      } else {
        await api.post('/production-orders/bulk-delete', {
          ids: Array.from(selectedIds)
        });
      }
      setSelectedIds(new Set());
      setIsAllSelected(false);

      const newTotal = total - count;
      const newMaxPages = Math.ceil(newTotal / PER_PAGE) || 1;
      if (page > newMaxPages) {
        setPage(newMaxPages);
      } else {
        setRefreshKey(prev => prev + 1);
      }
    } catch (err) {
      alert(err.message || 'Failed to delete production orders');
    }
  };

  const displayCount = isAllSelected ? total : selectedIds.size;

  return (
    <ListPageShell>
      <ListPageHeader
        title="Production Orders"
        subtitle={`${total} record${total !== 1 ? 's' : ''}`}
        action={
          <div className="flex items-center gap-2">
            {isSuperAdmin && displayCount > 0 && (
              <button type="button" onClick={handleBulkDelete} className="btn-danger">
                Delete Selected ({displayCount})
              </button>
            )}
            <button type="button" onClick={() => navigate('/production-order/new')} className="btn-primary">
              + Add New
            </button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-3 items-center">
        <ListSearchBar value={search} onChange={applySearch} placeholder="Search order #, item, description…" />
        <ListFilterSelect value={statusFilter} onChange={applyStatus}>
          <option value="">All Statuses</option>
          {STATUS_OPTS.map((s) => <option key={s} value={s}>{s}</option>)}
        </ListFilterSelect>
      </div>

      <div className="data-table-wrap">
        <table className="w-full text-sm">
          <thead>
            <tr className="table-head">
              {isSuperAdmin && (
                <th className="text-center px-4 py-3 w-12">
                  <input
                    type="checkbox"
                    checked={isAllSelected || (orders.length > 0 && orders.every(o => selectedIds.has(o.prod_order_id)))}
                    onChange={handleSelectAll}
                    className="rounded border-gray-300 text-sap-blue focus:ring-sap-blue cursor-pointer"
                  />
                </th>
              )}
              <SortTh sort={sort} field="order_no" className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider" align="left">Order #</SortTh>
              <SortTh sort={sort} field="item_no" className="text-left px-4 py-3 font-semibold text-2xs uppercase tracking-wider" align="left">Product</SortTh>
              <SortTh sort={sort} field="department" className="text-center px-4 py-3 font-semibold text-2xs uppercase tracking-wider" align="center">Dept.</SortTh>
              <SortTh sort={sort} field="planned_qty" className="text-right px-4 py-3 font-semibold text-2xs uppercase tracking-wider" align="right">Qty</SortTh>
              <SortTh sort={sort} field="due_date" className="text-center px-4 py-3 font-semibold text-2xs uppercase tracking-wider" align="center">Due Date</SortTh>
              <SortTh sort={sort} field="status" className="text-center px-4 py-3 font-semibold text-2xs uppercase tracking-wider" align="center">Status</SortTh>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isSuperAdmin ? 7 : 6} className="px-4 py-8 text-center text-sap-textLight">Loading…</td></tr>
            ) : orders.length === 0 ? (
              <tr><td colSpan={isSuperAdmin ? 7 : 6} className="px-4 py-8 text-center text-sap-textLight">No production orders found.</td></tr>
            ) : sort.rows.map((o) => (
              <tr
                key={o.prod_order_id}
                onClick={() => navigate(`/production-order/${o.prod_order_id}`)}
                className="table-row even:bg-gray-50/50"
              >
                {isSuperAdmin && (
                  <td className="px-4 py-3 text-center w-12" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isAllSelected || selectedIds.has(o.prod_order_id)}
                      onChange={() => handleSelectRow(o.prod_order_id)}
                      className="rounded border-gray-300 text-sap-blue focus:ring-sap-blue cursor-pointer"
                    />
                  </td>
                )}
                <td className="px-4 py-3 font-semibold text-sap-blue">{o.order_no}</td>
                <td className="px-4 py-3">{o.item_no} — {o.description}</td>
                <td className="px-4 py-3 text-center text-sap-textLight">{o.department || '—'}</td>
                <td className="px-4 py-3 text-right font-semibold">{Number(o.planned_qty)}</td>
                <td className="px-4 py-3 text-center text-sap-textLight">{o.due_date ? o.due_date.slice(0, 10) : '—'}</td>
                <td className="px-4 py-3 text-center">
                  {o.status === 'Released' || o.status === 'In Progress' ? (
                    <span className="badge-active">{o.status}</span>
                  ) : (
                    <span className={`px-2.5 py-0.5 rounded-lg text-xs font-semibold ${statusStyle(o.status)}`}>{o.status}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ListPagination page={page} totalPages={totalPages} total={total} onPageChange={setPage} />
    </ListPageShell>
  );
}